import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useNavigation } from '../context/NavigationContext';
import { formatDocId } from '.././utils/validateInputs'
import './personalDataForm.css';
import ProgressBar from '../components/ProgressBar';

const apiUrl = process.env.REACT_APP_API_URL;
const apiKey = process.env.REACT_APP_API_KEY!;

// Funciones de validación

const validateRUT = (value?: string): boolean => {
  if (!value) {
    return false; // El RUT es obligatorio
  }
  value = value.replace(/\./g, '').replace(/-/g, '');
  if (value.length < 8 || value.length > 9) return false;

  const rut = value.slice(0, -1);
  const dv = value.slice(-1).toUpperCase();

  let total = 0;
  let multiplier = 2;

  for (let i = rut.length - 1; i >= 0; i--) {
    total += parseInt(rut[i]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }

  let expectedDV = 11 - (total % 11);
  let expectedDvString = expectedDV === 11 ? '0' : expectedDV === 10 ? 'K' : expectedDV.toString();

  return dv === expectedDvString;
};

const validatePassport = (value?: string): boolean => {
  if (!value) {
    return false;
  }
  return true;
  // const passportRegex = /^[A-Z]{2}\d{7}$/;
  // return passportRegex.test(value);
};

interface HealthInsuranceList {
  cod: string;
  name: string;
}

interface GetInitialUser {
  docType: string,
  docId: string,
  birthdate?: string,
  healthInsurance?: string,
}

interface InitialUser {
  docType: string,
  docId: string,
  birthdate?: string,
  healthInsurance?: string,
  name: string,
  phone: string,
  secondaryPhone: string,
  email: string,
  lastName: string,
  motherLastName: string,
}

// Componente del formulario
const PersonalDataForm: React.FC = () => {
  const [document, setDocument] = useState('');
  const [healthInsuranceList, setHealtInsuranceList] = useState<HealthInsuranceList[]>([])
  const { user, setUser } = useUser();
  const navigate = useNavigate();
  const { handleNavigationFromRoot } = useNavigation();

  useEffect(() => {
    if (user.docType !== '') {
      setDocument(user.docType)
    }
  }, [user.docType])

  useEffect(() => {
    const fetchMedicalPlans = async () => {
      try {
        const response = await fetch(
          `${apiUrl}/api/get-medical-plan-types`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-API-KEY': apiKey
          }
        })
        const data = await response.json();
        setHealtInsuranceList(data.data);
      } catch (error) {
        console.error('Error al obtener los nombres:', error);
      }
    };

    fetchMedicalPlans();
  }, []);


  const savePatientData = (values: InitialUser) => {
    setUser({
      ...user,
      ...values,
    });
  }

  const fetchPatientData = async (values: GetInitialUser) => {
    const docId = formatDocId(values);
    try {
      const url = new URL(`${apiUrl}/api/get-patient`);
      url.searchParams.append('docType', values.docType);
      url.searchParams.append('docNumber', docId);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': apiKey
        }
      });

      const data = await response.json();

      let patienData = {
        docType: values.docType,
        docId: values.docId,
        birthdate: '',
        healthInsurance: values.healthInsurance,
        name: '',
        phone: '',
        secondaryPhone: '',
        email: '',
        lastName: '',
        motherLastName: '',
        patientCod: ''
      }

      if (data.data && data.data.patientNames) {
        patienData = {
          docType: values.docType,
          docId: values.docId,
          birthdate: '12/12/1990',
          healthInsurance: values.healthInsurance,
          name: data.data.patientNames,
          phone: data.data.patientPhonePrimary,
          secondaryPhone: data.data.patientPhoneSecondary,
          email: data.data.patientEmail,
          lastName: data.data.patientFatherLastname,
          motherLastName: data.data.patientMotherLastname,
          patientCod: data.data.patientCod
        }
      }
      await savePatientData(patienData)
      navigate('/date');
    } catch (error) {
      console.error('Error al obtener información del paciente', error);
    }
  };


  const validatedDocId = (value?: string) => {
    if (document === "rut") {
      return validateRUT(value);
    }
    if (document === "passport") {
      return validatePassport(value);
    }
  };
  // Esquema de validación con Yup
  const SignupSchema = Yup.object().shape({
    docType: Yup.string()
      .required('El documento de identificación es obligatorio')
      .oneOf(['rut', 'passport'], 'Documento de Identificación no válido'),
    docId: Yup.string()
      .required('El número de documento es obligatorio')
      .test('is-valid-rut', 'El número de documento no es válido', validatedDocId),
    healthInsurance: Yup.string()
      .required('La previsión de salud es obligatoria')
  });

  const initialValues = {
    docType: user.docType || '',
    docId: user.docId || '',
    healthInsurance: user.healthInsurance || '',
  };

  return (
    <>
      <ProgressBar loadPercentage={25} />
      <div className='personal-data-form'>
        <h3>Para tomar su hora web, ingrese la siguiente información.</h3>
        <Formik
          initialValues={initialValues}
          validationSchema={SignupSchema}
          onSubmit={(values) => {
            handleNavigationFromRoot();
            fetchPatientData(values);
          }}
        >
          {({ setFieldValue, values }) => (
            <Form>
              <div className='personal-data-item'>
                <label htmlFor='docType'>Documento de Identificación</label>
                <Field
                  as='select'
                  name='docType'
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                    const { value } = event.target;
                    setFieldValue('docType', value);
                    setFieldValue('docId', '');
                    setDocument(value);
                  }}
                >
                  <option value=''>Seleccione</option>
                  <option value='rut'>RUT</option>
                  <option value='passport'>Pasaporte</option>
                </Field>
                <ErrorMessage className='error-message' name='docType' component='div' />
              </div>
              {values.docType !== "" && (
                <>
                  <div className='personal-data-item'>
                    <label htmlFor={values.docType}>
                      {values.docType === 'rut' ? 'Número de RUT' : 'Número de pasaporte'}
                    </label>
                    <Field name='docId' type='text' />
                    <ErrorMessage className='error-message' name='docId' component='div' />
                  </div>
                </>
              )
              }
              <div className='personal-data-item'>
                <label htmlFor='healthInsurance'>Previsión de salud</label>
                <Field as='select' name='healthInsurance'>
                  <option value=''>Seleccione previsión</option>
                  {healthInsuranceList.length > 0 && (
                    healthInsuranceList.map((insurance) => (
                      <option key={insurance.cod} value={insurance.cod}>
                        {insurance.name}
                      </option>
                    ))
                  )}
                </Field>
                <ErrorMessage className='error-message' name='healthInsurance' component='div' />
              </div>

              <button type='submit'>Continuar</button>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default PersonalDataForm;